/**
 * Knowledge Base Loader
 * 
 * Funktioner för att läsa kunskapsbasartiklar från Markdown-filer
 * och metadata från JSON.
 */

import knowledgeIndex from '@/data/knowledge-base/index.json';

// Typdefinitioner baserade på JSON-schemat
export type ArticleLevel = '7d' | '30d' | '90d';

export interface Article {
  id: string;
  title: string;
  summary: string;
  readTime: number;
  level: ArticleLevel;
  file: string;
}

export interface AreaMetadata {
  title: string;
  description: string;
  keyPoints: string[];
  articles: Article[];
}

export type AreaKey = 
  | 'introduction'
  | 'water'
  | 'food'
  | 'shelter'
  | 'health'
  | 'communication'
  | 'information'
  | 'energy'
  | 'safety'
  | 'transport';

export interface KnowledgeIndex {
  version: string;
  lastUpdated: string;
  areas: Record<AreaKey, AreaMetadata>;
}

const articleLevelOrder: Record<ArticleLevel, number> = {
  '7d': 0,
  '30d': 1,
  '90d': 2,
};

function sortArticlesByLevel(articles: Article[]): Article[] {
  return articles
    .map((article, index) => ({ article, index }))
    .sort((left, right) => {
      const levelDifference = articleLevelOrder[left.article.level] - articleLevelOrder[right.article.level];

      if (levelDifference !== 0) {
        return levelDifference;
      }

      return left.index - right.index;
    })
    .map(({ article }) => article);
}

const rawKnowledgeIndex = knowledgeIndex as KnowledgeIndex;

const sortedKnowledgeAreas = Object.fromEntries(
  Object.entries(rawKnowledgeIndex.areas).map(([areaKey, area]) => [
    areaKey,
    {
      ...area,
      articles: sortArticlesByLevel(area.articles),
    },
  ])
) as Record<AreaKey, AreaMetadata>;

const sortedKnowledgeIndex: KnowledgeIndex = {
  ...rawKnowledgeIndex,
  areas: sortedKnowledgeAreas,
};

/**
 * Hämtar alla områden med metadata
 */
export function getAllAreas(): Record<AreaKey, AreaMetadata> {
  return sortedKnowledgeIndex.areas;
}

/**
 * Hämtar metadata för ett specifikt område
 */
export function getAreaMetadata(areaKey: AreaKey): AreaMetadata | null {
  return (knowledgeIndex as KnowledgeIndex).areas[areaKey] || null;
}

/**
 * Hämtar en artikel med dess innehåll
 */
export async function getArticleContent(articleId: string): Promise<string> {
  try {
    // Hitta vilken fil artikeln finns i genom att söka i alla områden
    const areas = getAllAreas();
    let articleFile: string | null = null;

    for (const area of Object.values(areas)) {
      const article = area.articles.find(a => a.id === articleId);
      if (article) {
        articleFile = article.file;
        break;
      }
    }

    if (!articleFile) {
      console.error(`Article ${articleId} not found in metadata`);
      return `# Artikel ej hittad\n\nArtikeln med ID "${articleId}" kunde inte hittas.`;
    }

    // Importera Markdown-filen dynamiskt
    // Ta bort .md från filnamnet om det finns, sedan lägg till det i import-strängen
    const baseFilename = articleFile.replace(/\.md$/, '');
    const module = await import(`@/data/knowledge-base/articles/${baseFilename}.md?raw`);
    return module.default || module;
  } catch (error) {
    console.error(`Error loading article ${articleId}:`, error);
    return `# Fel vid laddning\n\nKunde inte ladda artikel "${articleId}". Kontrollera att filen finns.`;
  }
}

/**
 * Hämtar metadata för en specifik artikel
 */
export function getArticleMetadata(articleId: string): (Article & { areaKey: AreaKey }) | null {
  const areas = getAllAreas();
  
  for (const [areaKey, area] of Object.entries(areas)) {
    const article = area.articles.find(a => a.id === articleId);
    if (article) {
      return {
        ...article,
        areaKey: areaKey as AreaKey,
      };
    }
  }
  
  return null;
}

/**
 * Hämtar alla artiklar för ett område
 */
export function getArticlesForArea(areaKey: AreaKey): Article[] {
  const area = getAreaMetadata(areaKey);
  return area?.articles || [];
}

/**
 * Söker efter artiklar baserat på sökterm
 */
export function searchArticles(searchTerm: string): Array<Article & { areaKey: AreaKey }> {
  const areas = getAllAreas();
  const results: Array<Article & { areaKey: AreaKey }> = [];
  const lowerSearch = searchTerm.toLowerCase();

  for (const [areaKey, area] of Object.entries(areas)) {
    for (const article of area.articles) {
      if (
        article.title.toLowerCase().includes(lowerSearch) ||
        article.summary.toLowerCase().includes(lowerSearch)
      ) {
        results.push({
          ...article,
          areaKey: areaKey as AreaKey,
        });
      }
    }
  }

  return results;
}
